import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { vendorsApi } from '../../api';
import { Vendor } from '../../types';
import { formatDate } from '../../utils';

interface QBVendorsTabProps {
  clientId: string;
}

export const QBVendorsTab: React.FC<QBVendorsTabProps> = ({ clientId }) => {
  const toast = useRef<Toast>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadVendors();
  }, [clientId]);

  const loadVendors = async () => {
    try {
      setIsLoading(true);
      const data = await vendorsApi.getVendors(clientId);
      // Only show vendors with QB IDs
      setVendors(data.filter(v => v.qbo_vendor_id));
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load vendors',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const qbIdBodyTemplate = (rowData: Vendor) => {
    if (rowData.qbo_vendor_id) {
      return rowData.qbo_vendor_id;
    }
    return <Tag value="Local only" severity="secondary" />;
  };

  const activeBodyTemplate = (rowData: Vendor) => {
    if (rowData.is_active) {
      return <Tag value="Active" severity="success" />;
    }
    return <Tag value="Inactive" severity="warning" />;
  };

  return (
    <>
      <Toast ref={toast} />

      <DataTable
        value={vendors}
        dataKey="id"
        loading={isLoading}
        paginator
        rows={50}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> vendors
            </span>
          ),
        }}
        size="small"
        stripedRows
        scrollable
        scrollHeight="flex"
        emptyMessage="No vendors found"
        sortField="name"
        sortOrder={1}
      >
        <Column
          field="name"
          header="Name"
          sortable
          style={{ width: '30%' }}
        />
        <Column
          field="qbo_vendor_id"
          header="QB ID"
          body={qbIdBodyTemplate}
          sortable
          style={{ width: '20%' }}
        />
        <Column
          field="is_active"
          header="Status"
          body={activeBodyTemplate}
          sortable
          style={{ width: '15%' }}
        />
        <Column
          field="created_at"
          header="Created"
          body={(rowData) => formatDate(rowData.created_at)}
          sortable
          style={{ width: '17.5%' }}
        />
        <Column
          field="updated_at"
          header="Updated"
          body={(rowData) => formatDate(rowData.updated_at)}
          sortable
        />
      </DataTable>
    </>
  );
};
